import { Badge } from "@/components/ui/badge";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatShortDateTime } from "@/services/time";
import type { KnockMessage } from "@/types/zod/knock";
import {
	Check,
	Eye,
	Mail,
	MousePointerClick,
	Send,
	Smartphone,
} from "lucide-react";
import { removeKnock, removeUrls } from "../utils/utils";
import { MessageBadge } from "./MessageBadge";

interface Props {
	message: KnockMessage;
	className?: string;
}

export function MessageCard({ message, className }: Props) {
	if (message.channel_type === "in-app") {
		return null;
	}

	if (
		message.status?.toLowerCase() === "queue" ||
		message.status?.toLowerCase() === "queued"
	) {
		return null;
	}

	if (
		!message.content?.data ||
		(message.content.data.__typename === "MessageSmsContent" &&
			!message.content.data.body) ||
		(message.content.data.__typename === "MessageEmailContent" &&
			!message.content.data.text_body &&
			!message.content.data.body)
	) {
		return null;
	}

	const getChannelIcon = (type?: string) => {
		switch (type) {
			case "email":
				return <Mail className="w-6 h-6 text-blue-500" />;
			case "sms":
				return <Smartphone className="w-6 h-6 text-green-500" />;
			default:
				return null;
		}
	};

	const cleanMessageText = (text: string | undefined | null) => {
		if (!text) return "";
		return removeKnock(removeUrls(text));
	};

	const getMessageContent = () => {
		if (!message.content?.data) return null;

		const content = message.content.data;

		if (content.__typename === "MessageEmailContent") {
			return (
				<div className="space-y-3">
					<div className="space-y-2">
						<div className="space-y-1 text-slate-700 text-sm">
							<div className="flex items-center gap-2">
								<span className="">To:</span>
								<span className="font-medium">{recipient}</span>
							</div>
							<div className="flex items-center gap-2">
								<span className="">From:</span>
								<span className="font-medium">{content.from?.name}</span>
								<span className="">({content.from?.email})</span>
							</div>
							{content.subject_line && (
								<div className="flex items-center gap-2">
									<span className="">Subject:</span>
									<span className="font-medium">
										{cleanMessageText(content.subject_line)}
									</span>
								</div>
							)}
						</div>
					</div>
					<div className="pt-3 border-t text-sm whitespace-pre-wrap">
						{cleanMessageText(content.text_body || content.body)}
					</div>
				</div>
			);
		}

		if (content.__typename === "MessageSmsContent") {
			return (
				<div className="space-y-2">
					<div className="text-slate-700 text-sm">
						<div className="flex items-center gap-2">
							<span className="">To:</span>
							<span className="font-medium">{recipient}</span>
						</div>
					</div>
					<div className="flex justify-end">
						<div className="bg-green-100 p-3 rounded-2xl rounded-tr-none max-w-[80%]">
							<div className="text-sm">{cleanMessageText(content.body)}</div>
						</div>
					</div>
				</div>
			);
		}

		return null;
	};

	const recipient = message.content?.data?.to || message.recipient;

	return (
		<div className={cn("rounded-lg border p-4 shadow-sm", className)}>
			<div className="space-y-3">
				<div className="flex justify-between items-center gap-4 w-full">
					<div className="flex items-center gap-2">
						{getChannelIcon(message.channel_type)}
						<div className="text-slate-600 text-xs">
							{formatShortDateTime(message.inserted_at)}
						</div>
					</div>
					<div className="flex items-center gap-2">
						<MessageBadge message={message} />
					</div>
				</div>

				{message.content && <div className="">{getMessageContent()}</div>}
			</div>
		</div>
	);
}
